<svelte:options
  customElement={{
    tag: "oc-cinema-v1",
    shadow: "none",
    /*                                            */
    extend: window.__components.extend({ delegateFocus: true }),
    props: {
      arrowButtonPosition: { type: "String", attribute: "arrow-button-position" },
      ocAriaLabel: { type: "String", attribute: "oc-aria-label" },
      stretchItems: { type: "Boolean", attribute: "stretch-items" },
      paginationDots: { type: "Boolean", attribute: "pagination-dots" },
      itemVisibilityThreshold: { type: "Array", attribute: "item-visibility-threshold" },
      cut: { type: "String", attribute: "cut" },
      gap: { type: "String", attribute: "gap" },
    },
  }}
/>

<script lang="ts">
  import { onMount, tick } from "svelte";

  import { useEventDispatcher } from "@otto-ec/otto-components-utils/use/event-dispatcher";
  import { refireScrollEvent } from "../../../common/actions/refireScrollEvent";
  import { ArrowButtonV1 } from "../../../common/components/ArrowButton";
  import PaginationDotsV1 from "../../../common/components/PaginationDotsV1/PaginationDotsV1.svelte";

  import type { Events, Props } from "./CinemaV1.types";
  import { calcItemsPerSlide } from "./math/calcItemsPerSlide";
  import { calcScrollPositions } from "./math/calcScrollPositions";

  type CinemaItemHTMLElement = {
    isVisible?: boolean;
    trackingIsVisible?: boolean;
  } & HTMLElement;

  let {
    ocAriaLabel,
    arrowButtonPosition,
    resizing = "stretch-cut",
    paginationDots = false,
    itemVisibilityThreshold = undefined,
    visibleItems = [],
    cut = undefined,
    gap = undefined,
  }: Props = $props();

  const Host = $host();

  const dispatch = useEventDispatcher<Events>(Host);

  let stage = $state<HTMLElement>();
  let prevCurrentItemIndex: number = 0;
  let currentItemIndex = 0;
  let items = $state(Array.from(Host.children) as CinemaItemHTMLElement[]);

  let stageClientWidth: number = $state(1);
  let stageScrollLeft: number = $state(0);
  let stageScrollWidth = $state(0);

  /*                                                                                                                       */
  /*                          */
  /*                                                         */
  /*                                                      */
  let update = $derived(([stageClientWidth, stageScrollLeft, items] && Date.now()) as number);

  $effect(() => {
    if (stage && update) {
      /*                                                    */
      tick().then(() => {
        stageScrollWidth = stage!.scrollWidth;
      });
    }
  });

  let itemGap = $derived(update && parseFloat(getComputedStyle(Host).getPropertyValue("--gap")));
  let itemCut = $derived(update && parseFloat(getComputedStyle(Host).getPropertyValue("--cut")));

  let hideLeftArrow = $derived(!!update && stageScrollLeft <= itemGap);
  let hideRightArrow = $derived(
    !!update && stageScrollLeft >= stageScrollWidth - stageClientWidth - itemGap,
  );

  let scrollPoints = $derived(
    stageScrollWidth ? calcScrollPositions(items, stageClientWidth, itemGap, itemCut) : [0],
  );

  let pageTransitionsPoints = $derived(
    scrollPoints.map((p, i) => ((scrollPoints[i - 1] ?? 0) + p) / 2),
  );

  let currentSlide = $derived(
    Math.max(
      pageTransitionsPoints.findIndex((point) => point >= stageScrollLeft) - 1,
      /*                                               */
      hideRightArrow ? scrollPoints.length - 1 : 0,
    ),
  );

  let itemsPerSlide = $derived(calcItemsPerSlide(stageClientWidth, itemGap, itemCut));

  /*                                                                                         */
  let stretchItems = $derived(
    resizing === "stretch" || (resizing === "stretch-cut" && itemsPerSlide >= items.length),
  );

  const moveTo = (targetSlide: number) => {
    stage!.scrollLeft =
      scrollPoints[Math.max(targetSlide, 0)] ?? scrollPoints[scrollPoints.length - 1];
  };

  let intersectingTimer = -1;

  /*                                           */
  const intersectingFinished = () => {
    if (currentItemIndex === prevCurrentItemIndex) return;

    if (itemVisibilityThreshold !== undefined) {
      dispatch("oc-item-visibility", {
        visibleItems: items.filter((item) => item.trackingIsVisible),
        scrollDirection: currentItemIndex > prevCurrentItemIndex ? "right" : "left",
      });
    } else {
      dispatch("oc-item-visibility", {
        visibleItems: items.filter((item) => item.isVisible),
        scrollDirection: currentItemIndex > prevCurrentItemIndex ? "right" : "left",
      });
    }

    prevCurrentItemIndex = currentItemIndex;
  };

  const cinemaIntersecting = () => {
    clearTimeout(intersectingTimer);

    intersectingTimer = window.setTimeout(intersectingFinished, 500);
  };

  onMount(() => {
    /*                                                                  */
    let slideVisibilityIntersectionObserver: IntersectionObserver;
    if (itemVisibilityThreshold) {
      slideVisibilityIntersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const target = entry.target as CinemaItemHTMLElement;
            target.trackingIsVisible = entry.isIntersecting;
          });

          visibleItems = items.filter((item) => item.trackingIsVisible);

          cinemaIntersecting();
        },
        { root: stage, threshold: itemVisibilityThreshold },
      );

      items.forEach((i) => slideVisibilityIntersectionObserver.observe(i));
    }

    let lastIntersectingItem: CinemaItemHTMLElement | null = null;

    /*                                                            */
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const target = entry.target as CinemaItemHTMLElement;
          target.isVisible = entry.isIntersecting;
          if (entry.isIntersecting) {
            lastIntersectingItem = target;
          }
        });

        currentItemIndex = items.findIndex((i) => i.isVisible);
        if (currentItemIndex === -1) {
          /*                                                            */
          currentItemIndex = items.findIndex((i) => i === lastIntersectingItem);
        }

        if (!itemVisibilityThreshold) {
          visibleItems = items.filter((item) => item.isVisible);
          cinemaIntersecting();
        }
      },
      { root: stage, threshold: [0.999, 1] },
    );

    items.forEach((i) => intersectionObserver.observe(i));

    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach(({ removedNodes, addedNodes }) => {
        addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            intersectionObserver.observe(node);
            slideVisibilityIntersectionObserver?.observe(node);
          }
        });
        removedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            intersectionObserver.unobserve(node);
            slideVisibilityIntersectionObserver?.unobserve(node);
          }
        });
        items = Array.from(Host.children) as CinemaItemHTMLElement[];
      });
    });

    mutationObserver.observe(Host, {
      childList: true,
    });

    return () => {
      intersectionObserver.disconnect();
      mutationObserver.disconnect();
      slideVisibilityIntersectionObserver?.disconnect();
    };
  });
</script>

<div
  class="cinema"
  style:--arrow-button-position={arrowButtonPosition}
  style:--cut={cut}
  style:--gap={gap}
>
  <section
    class={`cinema__stage cinema__stage--${resizing}`}
    class:cinema__stage--stretch={stretchItems}
    class:cinema__stage--cut={resizing === "stretch-cut" || resizing === "cut"}
    style:--items-per-slide={itemsPerSlide}
    bind:this={stage}
    bind:clientWidth={stageClientWidth}
    use:refireScrollEvent={Host}
    onscroll={() => {
      /*                                          */
      stageScrollLeft = stage.scrollLeft;
    }}
    aria-label={ocAriaLabel}
    data-oc-floating-focus-v1-target
  >
    <slot />
  </section>
  <ArrowButtonV1 hidden={hideLeftArrow} direction="left" onclick={() => moveTo(currentSlide - 1)} />
  <ArrowButtonV1
    hidden={hideRightArrow}
    direction="right"
    onclick={() => moveTo(currentSlide + 1)}
  />
</div>

{#if paginationDots}
  <PaginationDotsV1 amountDots={scrollPoints.length} selectedDot={currentSlide} {moveTo} />
{/if}

<style lang="scss" global>
  @use "@otto-ec/otto-components-utils/scss/mixins";
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "math/calcItemBase" as *;

  :host {
    /*        */
    --gap: #{tokens.$oc-component-cinema-stage-gap-x};
    --cut: #{tokens.$oc-component-cinema-cut-item-visible-percentage};
    --arrow-button-position: 50%;

    @include mixins.no-tap-highlight();
    display: block;
    max-height: 50rem;
  }

  .cinema {
    position: relative;

    &__stage {
      display: flex;
      gap: var(--gap);
      overflow-x: auto;
      overflow-y: hidden;
      scrollbar-width: none; /*     */
      scroll-behavior: smooth;

      /*                                  */
      padding: 4px 0;
      margin: -4px 0;

      &::-webkit-scrollbar {
        display: none; /*                */
      }

      /*                                            */
      @media (prefers-reduced-motion) {
        scroll-behavior: auto;
      }

      &:before,
      &:after {
        content: "";
        flex: 0 0 0;
      }

      ::slotted(*) {
        flex: 0 0 auto;
        max-width: 100%;
      }

      &--cut {
        ::slotted(*) {
          flex: 0 0 calcItemBase(var(--items-per-slide), var(--gap), var(--cut));
        }
      }

      &--stretch {
        ::slotted(*) {
          flex: 1 0 auto;
        }
      }
    }
  }
</style>
